import type { WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useCallback } from 'react'
import { type NavigateOptions, useLocation, useNavigate } from 'react-router'

import { configurationItem, server as serverPath } from './paths'
import { useCurrentConfigurationItem } from './useCurrentConfigurationItem'
import { useNavigationParams } from './useNavigationParams'

export type WorkspaceNavigationMode = 'preserve' | 'reset'

export type WorkspaceNavigationOptions = NavigateOptions & {
  mode: WorkspaceNavigationMode
}

export type WorkspaceSwitchOptions = NavigateOptions & {
  mode?: WorkspaceNavigationMode
}

export const useWorkspaceNavigation = () => {
  const navigate = useNavigate()
  const { branch: currentBranch, layer, block, server } = useNavigationParams()
  const currentItem = useCurrentConfigurationItem()
  const { hash } = useLocation()

  return useCallback(
    async (
      toWorkspace: Optional<WorkspaceKey>,
      { mode, ...navigateOptions }: WorkspaceNavigationOptions,
    ) => {
      if (mode === 'preserve') {
        if (currentItem) {
          await navigate(
            `${configurationItem({
              server: toWorkspace?.branchKey.server ?? server,
              layer,
              block,
              workspaceName: toWorkspace?.name,
              branch: toWorkspace?.branchKey.name ?? currentBranch,
              ...currentItem,
            })}${hash}`,
            navigateOptions,
          )
        } else {
          await navigate(
            serverPath(
              toWorkspace
                ? {
                    server: toWorkspace.branchKey.server,
                    workspaceName: toWorkspace.name,
                    branch: toWorkspace.branchKey.name,
                    block,
                  }
                : {
                    server,
                    branch: currentBranch,
                    block,
                  },
            ),
            navigateOptions,
          )
        }
      } else {
        await navigate(
          serverPath({
            server: toWorkspace?.branchKey.server ?? server,
            workspaceName: toWorkspace?.name,
            branch: toWorkspace?.branchKey.name ?? currentBranch,
            block,
          }),
          navigateOptions,
        )
      }
    },
    [currentItem, hash, server, navigate, layer, currentBranch, block],
  )
}
